// A moved task's read, judged for FRESHNESS (#6772, review round 2). The note, Undo, Put
// everything back, the Moving / Moved tags and the resting rings all ask one question of a task
// this page moved: has the move it accepted landed? Batch history cannot answer it (history is
// pruned, and a batch accepted outside the page looks foreign); order can.
//
// A read counts only if its request STARTED after the 201 landed (`orderStamp`, one counter for
// both). An earlier read is ignored, whatever batch it shows: it may predate the move's commit.
// A fresh read showing the accepted batch is the move as it stands. A fresh read showing NO move
// is pending. A fresh read showing a DIFFERENT batch means a later batch of the task superseded
// this page's move (it may still be in transit, or have failed and left the task where it was): a
// read that started after the 201 cannot see an older batch, because the 201 came after the move
// was committed and a task's newest move only moves forward.

import { MACHINE_JOB_STATUSES } from '@developerz/domain';
import type { TaskMove } from './move-note';

/** A task read's `move`, and when its request started in the page's order (`orderStamp`). */
export type MoveRead = {
  readonly move: TaskMove | null | undefined;
  readonly order: number;
  /**
   * The task still holds a place on a machine (`MACHINE_JOB_STATUSES`: assigned, in progress,
   * paused, or pending). False once it finished or was dropped: it can never be put back.
   * Absent reads as held.
   */
  readonly held?: boolean;
  /**
   * The task's own status on the same read (`GET /v1/tasks/:id`'s `status`): the transfer stack
   * (#6773) reads a landed move as complete only once it is `in_progress`. Absent when unknown.
   */
  readonly status?: string | undefined;
};

/** What a page knows of one move it accepted. */
export type AcceptedMoveRef = {
  readonly batchId: string;
  /** When its 201 landed, in the page's order. */
  readonly order: number;
};

export type MoveJudgement =
  /** No read since the 201, or a fresh read with no move yet: not landed. */
  | { readonly kind: 'pending' }
  /** A fresh read of the accepted batch: its state as the server has it. */
  | { readonly kind: 'ours'; readonly move: TaskMove }
  /**
   * A fresh read of a LATER batch of the task, in whatever state (in transit, landed or failed):
   * this page's move is no longer its newest. It says nothing about where the task is; that is
   * `movePlace`'s, and a later batch that failed leaves the task on that batch's source.
   */
  | { readonly kind: 'superseded'; readonly move: TaskMove };

export function judgeMoveRead(read: MoveRead | undefined, ref: AcceptedMoveRef): MoveJudgement {
  if (read === undefined || read.order <= ref.order || read.move == null)
    return { kind: 'pending' };
  return read.move.batch_id === ref.batchId
    ? { kind: 'ours', move: read.move }
    : { kind: 'superseded', move: read.move };
}

/** A fresh read settled THIS page's move: landed, failed, or superseded by a later batch. */
export function moveSettled(j: MoveJudgement): boolean {
  if (j.kind === 'superseded') return true;
  return j.kind === 'ours' && (j.move.state === 'landed' || j.move.state === 'failed');
}

/** Where a moved task is, as its newest fresh read has it. */
export type MovePlace =
  /** No fresh read yet, or its newest move is still in transit: where it ends is not known. */
  | { readonly kind: 'pending' }
  /** Its newest move settled: on `runnerId` (null when the server names no machine). */
  | { readonly kind: 'at'; readonly runnerId: string | null }
  /** It no longer holds a place on any machine (finished or dropped): nothing to put back. */
  | { readonly kind: 'gone' };

/**
 * A task's location, DERIVED from its newest read alone (#6772 review round 8), never from a
 * 201 or from an earlier read: the read must have started after `order` (this page's newest
 * accepted move of it), and whatever batch it shows, this page's or another tab's, decides.
 * Landed: that batch's target. Failed: its source (the item never left). In transit: pending,
 * never its target. Each newer read replaces the answer; nothing is remembered between reads.
 */
export function movePlace(read: MoveRead | undefined, order: number): MovePlace {
  if (read === undefined || read.order <= order) return { kind: 'pending' };
  if (read.held === false) return { kind: 'gone' };
  if (read.move == null) return { kind: 'pending' };
  const m = read.move;
  if (m.state === 'landed') return { kind: 'at', runnerId: m.to_runner_id };
  if (m.state === 'failed') return { kind: 'at', runnerId: m.from_runner_id };
  return { kind: 'pending' };
}

/**
 * A task with `status` still holds a place on a machine (`MACHINE_JOB_STATUSES`: assigned, in
 * progress, paused, pending). Only a read that NAMES another status finished or dropped it; a
 * read naming none is not evidence it is gone.
 */
export function heldBy(status: string | undefined): boolean {
  return status === undefined || (MACHINE_JOB_STATUSES as readonly string[]).includes(status);
}
